import React, { useCallback, useMemo } from 'react';
import { Typography, Checkbox } from '@scale/scaleui';
import { MultiTurnInteractionConfiguration } from '@scale/shared/types/chat';
import produce from 'immer';
import _ from 'lodash';
import {
  ImageDatabaseSource,
  ImageLicense,
  SerpImageLicense,
  SerpImageSearchEngine,
  BingSerpImageLicense,
} from '@scale/mm-shared';
import { CheckboxCards, Flex, RadioGroup, Text } from '@scale/scaleui-radix';

const NO_LICENSE_FILTER = 'No License Filter';

interface ImageServiceConfiguratorProps {
  existingValue: MultiTurnInteractionConfiguration;
  updateProperty: (arg: MultiTurnInteractionConfiguration) => void;
}

/* eslint-disable complexity */
const ImageServiceConfigurator = ({
  existingValue,
  updateProperty,
}: ImageServiceConfiguratorProps) => {
  const config = existingValue;

  const addLicenseValue = useCallback(
    (license: ImageLicense, path: string) => {
      const updatedConfig = produce(config, (draft: MultiTurnInteractionConfiguration) => {
        const fullpath = `imageValidatorSettings.${path}`;
        if (!_.has(draft, fullpath)) {
          _.set(draft, fullpath, []);
        }
        if (_.indexOf(_.get(draft, fullpath), license) >= 0) {
          return;
        }
        _.get(draft, fullpath).push(license);
      });
      updateProperty(updatedConfig);
    },
    [config, updateProperty],
  );

  const removeLicenseValue = useCallback(
    (license: ImageLicense, path: string) => {
      const updatedConfig = produce(config, (draft: MultiTurnInteractionConfiguration) => {
        const fullpath = `imageValidatorSettings.${path}`;
        if (_.has(draft, fullpath)) {
          const idx = _.indexOf(_.get(draft, fullpath), license);
          if (idx >= 0) {
            _.get(draft, fullpath).splice(idx, 1);
          }
        }
      });
      updateProperty(updatedConfig);
    },
    [config, updateProperty],
  );

  const setLicenseValueSerp = useCallback(
    (license: SerpImageLicense | typeof NO_LICENSE_FILTER) => {
      const updatedConfig = produce(config, (draft: MultiTurnInteractionConfiguration) => {
        if (license === NO_LICENSE_FILTER) {
          _.set(draft, 'imageValidatorSettings.allowedLicenseSerp', undefined);
        } else {
          _.set(draft, 'imageValidatorSettings.allowedLicenseSerp', license);
        }
      });
      updateProperty(updatedConfig);
    },
    [config, updateProperty],
  );

  const setLicenseValueBingSerp = useCallback(
    (license: BingSerpImageLicense | typeof NO_LICENSE_FILTER) => {
      const updatedConfig = produce(config, (draft: MultiTurnInteractionConfiguration) => {
        if (license === NO_LICENSE_FILTER) {
          _.set(draft, 'imageValidatorSettings.allowedLicenseBingSerp', undefined);
        } else {
          _.set(draft, 'imageValidatorSettings.allowedLicenseBingSerp', license);
        }
      });
      updateProperty(updatedConfig);
    },
    [config, updateProperty],
  );

  const setSerpSearchEngine = useCallback(
    (engine: SerpImageSearchEngine) => {
      const updatedConfig = produce(config, (draft: MultiTurnInteractionConfiguration) => {
        _.set(draft, 'serpSearchEngine', engine);
        // Reset license filters when switching engines
        _.set(draft, 'imageValidatorSettings.allowedLicenseSerp', undefined);
        _.set(draft, 'imageValidatorSettings.allowedLicenseBingSerp', undefined);
      });
      updateProperty(updatedConfig);
    },
    [config, updateProperty],
  );

  const setEnableImageDatabases = useCallback(
    (enabledDatabases: string[]) => {
      const updatedConfig = produce(config, (draft: MultiTurnInteractionConfiguration) => {
        _.set(draft, 'enabledImageDatabases', enabledDatabases);
      });
      updateProperty(updatedConfig);
    },
    [config, updateProperty],
  );

  const enabledImageDatabases = useMemo(
    () => config.enabledImageDatabases ?? Object.values(ImageDatabaseSource),
    [config.enabledImageDatabases],
  );

  const selectedSearchEngine = useMemo(
    () => config.serpSearchEngine ?? SerpImageSearchEngine.GOOGLE,
    [config.serpSearchEngine],
  );

  const isInternetSearchEnabled = useMemo(
    () => enabledImageDatabases.includes(ImageDatabaseSource.INTERNET),
    [enabledImageDatabases],
  );
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-2">
        <Typography.Paragraph2>
          Filter on Allowed Licenses for Scale Image Database
        </Typography.Paragraph2>
        {Object.values(ImageLicense).map(license => (
          <Checkbox
            key={license}
            checked={_.indexOf(config.imageValidatorSettings?.allowedLicenses ?? [], license) >= 0}
            onChange={e => {
              if (e.target.checked) {
                addLicenseValue(license, 'allowedLicenses');
              } else {
                removeLicenseValue(license, 'allowedLicenses');
              }
            }}
            label={license}
          />
        ))}
      </div>
      {isInternetSearchEnabled && (
        <>
          <div className="flex flex-col gap-2">
            <Typography.Paragraph2>
              Select Search Engine for Internet Image Search
            </Typography.Paragraph2>
            <RadioGroup.Root
              value={selectedSearchEngine}
              onValueChange={setSerpSearchEngine}
              name="serpSearchEngine"
            >
              {Object.values(SerpImageSearchEngine).map(engine => (
                <Text className="cursor-pointer" as="label" size="2" key={engine}>
                  <Flex gap="2">
                    <RadioGroup.Item value={engine} />{' '}
                    {engine === SerpImageSearchEngine.GOOGLE ? 'Google Images' : 'Bing Images'}
                  </Flex>
                </Text>
              ))}
            </RadioGroup.Root>
          </div>
          {selectedSearchEngine === SerpImageSearchEngine.GOOGLE && (
            <div className="flex flex-col gap-2">
              <Typography.Paragraph2>
                Select Allowed License for Google Image Search
              </Typography.Paragraph2>
              <RadioGroup.Root
                value={config.imageValidatorSettings?.allowedLicenseSerp ?? NO_LICENSE_FILTER}
                onValueChange={setLicenseValueSerp}
                name="allowedLicenseSerp"
              >
                {[...Object.values(SerpImageLicense), NO_LICENSE_FILTER].map(license => (
                  <Text className="cursor-pointer" as="label" size="2" key={license}>
                    <Flex gap="2">
                      <RadioGroup.Item value={license} /> {license}
                    </Flex>
                  </Text>
                ))}
              </RadioGroup.Root>
            </div>
          )}
          {selectedSearchEngine === SerpImageSearchEngine.BING && (
            <div className="flex flex-col gap-2">
              <Typography.Paragraph2>
                Select Allowed License for Bing Image Search
              </Typography.Paragraph2>
              <RadioGroup.Root
                value={config.imageValidatorSettings?.allowedLicenseBingSerp ?? NO_LICENSE_FILTER}
                onValueChange={setLicenseValueBingSerp}
                name="allowedLicenseBingSerp"
              >
                {[...Object.values(BingSerpImageLicense), NO_LICENSE_FILTER].map(license => (
                  <Text className="cursor-pointer" as="label" size="2" key={license}>
                    <Flex gap="2">
                      <RadioGroup.Item value={license} />{' '}
                      {toBingSerpImageLicenseLabel(license as BingSerpImageLicense)}
                    </Flex>
                  </Text>
                ))}
              </RadioGroup.Root>
            </div>
          )}
        </>
      )}
      <div className="flex flex-col gap-2">
        <Typography.Paragraph2>Enabled Image Databases (minimum 1)</Typography.Paragraph2>
        <div className="flex justify-start gap-2">
          <CheckboxCards.Root
            value={enabledImageDatabases}
            onValueChange={setEnableImageDatabases}
            className="flex flex-row flex-wrap gap-2"
          >
            {Object.values(ImageDatabaseSource).map(source => (
              <CheckboxCards.Item
                value={source}
                key={source}
                disabled={
                  // This logic needs to be updated if we add more databases. This is to ensure a user can't disable all databases.
                  enabledImageDatabases.includes(source) && enabledImageDatabases.length === 1
                }
              >
                {toImageDatabaseSourceLabel(source)}
              </CheckboxCards.Item>
            ))}
          </CheckboxCards.Root>
        </div>
      </div>
    </div>
  );
};

function toImageDatabaseSourceLabel(source: ImageDatabaseSource) {
  switch (source) {
    case ImageDatabaseSource.INTERNET:
      return 'Internet Image Search';
    case ImageDatabaseSource.ELASTICSEARCH:
      return 'Scale Image Database';
    default:
      return source;
  }
}

function toBingSerpImageLicenseLabel(license: BingSerpImageLicense | typeof NO_LICENSE_FILTER) {
  if (license === NO_LICENSE_FILTER) {
    return NO_LICENSE_FILTER;
  }
  switch (license) {
    case BingSerpImageLicense.TYPE_ANY:
      return 'All Creative Commons';
    case BingSerpImageLicense.L1:
      return 'Public Domain';
    case BingSerpImageLicense.L2_L3_L4_L5_L6_L7:
      return 'Free to share and use';
    case BingSerpImageLicense.L2_L3_L4:
      return 'Free to share and use commercially';
    case BingSerpImageLicense.L2_L3_L5_L6:
      return 'Free to modify, share and use';
    case BingSerpImageLicense.L2_L3:
      return 'Free to modify, share, and use commercially';
    default:
      return license;
  }
}

export default ImageServiceConfigurator;
